import React from 'react';

import { FormProvider } from 'react-hook-form';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { TitleWithCounter } from 'components/common/titleWithCounter/titleWithCounter';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { useKeyActivitiesLogic } from 'components/widgets/KeyActivities/KeyActivities.logic';
import classes from 'components/widgets/KeyActivities/KeyActivities.module.scss';
import { useKeyActivitiesProvider } from 'components/widgets/KeyActivities/KeyActivities.provider';
import { columnsV8, initialState } from 'components/widgets/KeyActivities/KeyActivities.utils';
import { Filter } from 'components/widgets/KeyActivities/components/filter';
import { ExportToCsvButton } from 'components/widgets/RiskRegister/components/ExportToCsvButton/ExportToCsvButton';

const title = 'Key Activities';
const id = 'key-activities';
const componentKey = 'keyActivities';

const KeyActivities = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: ITableWidgetWrapperProps) => {
    // Table
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    // Provider
    const { ui, api } = useKeyActivitiesProvider({
        componentKey,
        widgetId,
        contractId,
        latestVersionId,
        compareVersionId,
        globalFilterData,
        localFilters,
    });

    // Widget
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    // Logic
    const { logic } = useKeyActivitiesLogic({ globalFilterData, api, ui, table });

    return (
        <WidgetWithTitle
            title={<TitleWithCounter title={title} counter={logic.filteredData.length} />}
            tooltip={api.getKeyActivities.data?.info}
            id={id}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : (
                    <FormProvider {...ui.form} key={'form'}>
                        <Filter key={'filters'} contractId={contractId} projectId={projectId} />
                    </FormProvider>
                ),
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => table.setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    callBack={table.setWidgetColumns}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown title={title} key={'export'} subTitle={subTitle}>
                    <ExportToCsvButton
                        headers={logic.exportCsv.headers}
                        rows={logic.exportCsv.rows}
                        fileName={logic.exportCsv.fileName}
                    />
                </ExportDropdown>,
            ]}
        >
            {table.updatedInitialState && (
                <div data-testid={'key-activities-container'} className={classes.container}>
                    <TableV8
                        data={logic.filteredData}
                        columns={columnsV8}
                        noData={logic.noDataText}
                        isLoading={api.getKeyActivities.isFetching}
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnFilters={table.columnFilters}
                        setColumnFilters={table.setColumnFilters}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        manualSorting={false}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                        ref={table.tableRef}
                    />
                </div>
            )}

            <CustomTableColumnsModal
                onHideModal={() => table.setModalColumnsOpen(false)}
                showModal={table.modalColumnsOpen}
                widgetId={widgetId}
                componentKey={componentKey}
                tableInstance={table.tableInstance}
                initialState={initialState}
                columns={columnsV8}
                onChangeOrder={table.setColumnOrder}
                onChangeVisibility={table.setColumnVisibility}
            />
        </WidgetWithTitle>
    );
};
export default React.memo(KeyActivities);
